import { Heart, Sparkles } from 'lucide-react';
import Colors from '@/constants/colors';
import React from 'react';
import { useAppStore } from '@/lib/store';

interface CompactCardProps {
  id: string;
  imageUrl: string;
  imageAlt: string;
  title: string;
  description: string;
  subtitle?: string;
  badge?: {
    text: string;
    backgroundColor?: string;
    textColor?: string;
  };
  location?: string;
  promoted?: boolean;
  onCardClick?: () => void;
}

export default function CompactCard({
  id,
  imageUrl,
  imageAlt,
  title,
  description,
  subtitle,
  badge,
  location,
  promoted,
  onCardClick,
}: CompactCardProps) {
  // Subscribe to store for reactive updates
  const isFavorite = useAppStore((state) => state.isFavorite(id));
  const toggleFavorite = useAppStore((state) => state.toggleFavorite);
  
  return (
    <div 
      style={{ 
        ...styles.card, 
        ...(promoted ? styles.promotedCard : {}),
        cursor: onCardClick ? 'pointer' : 'default',
      }}
      onClick={onCardClick}
    >
      {promoted && (
        <div style={styles.promotedBanner}>
          <Sparkles size={14} color={Colors.accent} />
          <span style={styles.promotedText}>PROMOTED</span>
        </div>
      )}
      <img src={imageUrl} alt={imageAlt} style={styles.image} />
      <div style={styles.content}>
        {badge && (
          <div
            style={{
              ...styles.badge,
              backgroundColor: badge.backgroundColor || Colors.ice,
            }}
          >
            <span
              style={{
                ...styles.badgeText,
                color: badge.textColor || Colors.primaryDark,
              }}
            >
              {badge.text}
            </span>
          </div>
        )}
        <h3 style={styles.title}>{title}</h3>
        {subtitle && <p style={styles.subtitle}>{subtitle}</p>}
        {!subtitle && <p style={styles.description}>{description}</p>}
        {location && <p style={styles.location}>{location}</p>}
      </div>
      <button
        style={styles.favoriteButton}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          toggleFavorite(id);
        }}
        aria-label="Toggle favorite"
      >
        <Heart
          size={18}
          fill={isFavorite ? Colors.error : 'transparent'}
          color={Colors.surface}
        />
      </button>
    </div>
  );
}

const styles = {
  card: {
    display: 'flex',
    flexDirection: 'row' as const,
    backgroundColor: Colors.surface,
    borderRadius: '12px',
    overflow: 'hidden',
    boxShadow: '0 1px 4px rgba(0, 0, 0, 0.05)',
    position: 'relative' as const,
    height: '110px',
    transition: 'all 0.2s ease',
    width: '100%',
  } as React.CSSProperties,

  promotedCard: {
    borderWidth: '2px',
    borderStyle: 'solid',
    borderColor: Colors.accent,
    boxShadow: `0 1px 4px ${Colors.accent}26`,
  } as React.CSSProperties,

  promotedBanner: {
    position: 'absolute' as const,
    top: '8px',
    left: '8px',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    backgroundColor: Colors.accent + '20',
    padding: '4px 8px',
    borderRadius: '8px',
    zIndex: 10,
  } as React.CSSProperties,

  promotedText: {
    fontSize: '11px',
    fontWeight: '700',
    color: Colors.accent,
    textTransform: 'uppercase' as const,
  } as React.CSSProperties,

  image: {
    width: '110px',
    height: '100%',
    minHeight: '110px',
    objectFit: 'cover' as const,
    flexShrink: 0,
  } as React.CSSProperties,

  content: {
    flex: 1,
    padding: '10px 12px',
    paddingRight: '36px',
    display: 'flex',
    flexDirection: 'column' as const,
    justifyContent: 'flex-start',
  } as React.CSSProperties,

  badge: {
    alignSelf: 'flex-start',
    padding: '3px 8px',
    borderRadius: '8px',
    marginBottom: '6px',
  } as React.CSSProperties,

  badgeText: {
    fontSize: '10px',
    fontWeight: '700',
    textTransform: 'uppercase' as const,
  } as React.CSSProperties,

  title: {
    fontSize: '15px',
    fontWeight: '700',
    color: Colors.text,
    margin: 0,
    marginBottom: '4px',
    lineHeight: '20px',
    display: '-webkit-box',
    WebkitLineClamp: 1,
    WebkitBoxOrient: 'vertical' as const,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  } as React.CSSProperties,

  subtitle: {
    fontSize: '12px',
    color: Colors.textSecondary,
    lineHeight: '16px',
    margin: 0,
    marginBottom: '4px',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical' as const,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  } as React.CSSProperties,

  description: {
    fontSize: '12px',
    color: Colors.textSecondary,
    lineHeight: '16px',
    margin: 0,
    marginBottom: '4px',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical' as const,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  } as React.CSSProperties,

  location: {
    fontSize: '11px',
    fontWeight: '600',
    color: Colors.primary,
    margin: 0,
  } as React.CSSProperties,

  favoriteButton: {
    position: 'absolute' as const,
    top: '50%',
    right: '8px',
    transform: 'translateY(-50%)',
    backgroundColor: 'rgba(0,0,0,0.4)',
    border: 'none',
    borderRadius: '16px',
    padding: '6px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s ease',
    zIndex: 20,
  } as React.CSSProperties,
};
